import { LoadingButton } from '@mui/lab';
import { Box, Button, type SxProps, type Theme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { USER_APPROVE_STATUS } from '@/config/constants';
import { useStartContractMutation } from '@/apis/extended/productApi';
import { useProductStatus } from '@/hooks/useProductStatus';
import { Product, PRODUCT_APPROVE_STATUS } from '@/types/ProductType';

type ProductDetailExploreOfferButtonProps = {
  product: Product;
  inline?: boolean;
  containerSx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
  /** When true, do not render the View Contract CTA (e.g. shown in DealRoomHeader instead). */
  suppressViewContractLink?: boolean;
};

export const ProductDetailExploreOfferButton = ({
  product,
  inline = false,
  containerSx,
  buttonSx,
  suppressViewContractLink = false,
}: ProductDetailExploreOfferButtonProps) => {
  const user = product.user;
  const navigate = useNavigate();
  const { approveStatus } = useProductStatus(product);

  const [startContract, { isLoading }] = useStartContractMutation();

  if (
    user?.approveStatus !== USER_APPROVE_STATUS.APPROVED ||
    !user?.approvalAmount
  ) {
    return null;
  }
  if (approveStatus !== PRODUCT_APPROVE_STATUS.APPROVED) return null;

  const handleStartContract = async () => {
    await startContract(product._id).unwrap();
    navigate(`/contract/${product._id}`);
  };

  if (suppressViewContractLink && product.contractStarted && product.contract) {
    return null;
  }

  return (
    <Box
      sx={{
        py: inline ? 0 : 3,
        display: 'flex',
        justifyContent: inline ? 'flex-start' : 'center',
        alignItems: 'center',
        ...containerSx,
      }}
    >
      {product.contractStarted && product.contract ? (
        <Button
          href={`/contract/${product._id}`}
          startIcon={<i className="fa-light fa-binoculars" />}
          variant="contained"
          size="medium"
          sx={buttonSx}
        >
          View Contract
        </Button>
      ) : (
        <LoadingButton
          variant="contained"
          color="secondary"
          size="medium"
          loading={isLoading}
          onClick={handleStartContract}
          startIcon={<i className="fa-light fa-play" />}
          sx={buttonSx}
        >
          Explore an Offer
        </LoadingButton>
      )}
    </Box>
  );
};
